import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import gsap from 'gsap';

import texturePath from '../../assets/img/covers/armfoot.jpg';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/2d3047-bfd5d9-a799b7-c2ac1e-048a81');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.isAnimating = false;
        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[2], 10, 100);

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.setupSubjects();
    }

    setupCamera() {
        let VIEW_ANGLE = 50;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 1;
        let FAR_PLANE = 500;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(0, 0, 10);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    setupLight() {
        let hemiLight = new THREE.HemisphereLight(this.colourArr[2], this.colourArr[3], 2);
        hemiLight.position.set(0, 5, 0);
        this.scene.add(hemiLight);

        let light1 = new THREE.PointLight(this.colourArr[1], 1);
        light1.position.set(0, 3, -2);
        this.scene.add(light1);

        let light2 = new THREE.PointLight(this.colourArr[3], 1);
        light2.position.set(0, 0, 0);
        this.scene.add(light2);

        this.light = hemiLight; //used in the shader
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.rotateSpeed = 1.;
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.25;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 200;
        this.controls.minDistance = 30;
    }

    createMaterial() {
        const texture = new THREE.TextureLoader().load(texturePath);
        //get the matrices from camera
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
        this.camera.updateWorldMatrix();
        const viewMatrixCamera = this.camera.matrixWorldInverse.clone();
        const projectionMatrixCamera = this.camera.projectionMatrix.clone();
        const projPosition = this.camera.position.clone();

        let uniforms = {
            uTexture0: { value: texture },
            uViewMatrixCamera: { value: viewMatrixCamera },
            uProjectionMatrixCamera: { value: projectionMatrixCamera },
            uProjPosition: { value: projPosition },
            uColor: { value: this.colourArr[3] },
            uLightPos: { value: this.light.position },
            uAmbientCol: { value: this.colourArr[3] },
            uDiffuseColor: { value: this.colourArr[2] },
            uSpecColor: { value: this.colourArr[3] }
        };
        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: `
                uniform mat4 uViewMatrixCamera;
                uniform mat4 uProjectionMatrixCamera;
                uniform vec3 uLightPos;

                varying vec4 vWorldPosition;
                varying vec3 vNormal;
                varying vec4 vTexCoords;
                varying vec4 vColor;

                uniform vec3 uAmbientColor;
                uniform vec3 uDiffuseColor;
                uniform vec3 uSpecColor;
                const float shininess = 5.0;

                void main()
                {
                    vNormal = mat3(modelMatrix) * normal;
                    vWorldPosition = modelMatrix * vec4(position, 1.0);
                    //texture projection
                    vTexCoords = uProjectionMatrixCamera * uViewMatrixCamera * vWorldPosition;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

                    //compute lighting
                    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
                    vec3 viewPos = viewPosition.xyz;
                    vec3 normViewDir = -normalize(viewPos);
                    vec3 normLightDir = normalize(uLightPos - viewPos);
                    vec4 ambientLight = vec4(uAmbientColor, 1.0);
                    //D = L * Mat * dot(Light, Normal) 
                    vec4 diffuseLight = vec4(max(dot(normLightDir, vNormal), 0.0) * uDiffuseColor, 1.0);

                    //S = L * Mat * dot(Reflection,Eye)pow(shininess) 
                    vec3 refl = reflect(-normLightDir, vNormal);
                    vec4 specularLight = vec4(0.0);
                    if(dot(normLightDir, normViewDir) > 0.0) 
                    {
                        specularLight = vec4(pow(min(dot(refl, normViewDir), 0.0), shininess) * uSpecColor, 1.0);
                    }
                    vColor = ambientLight + diffuseLight + specularLight;
                }
            `,
            fragmentShader: `
                precision mediump float;
                uniform sampler2D uTexture0;
                uniform vec3 uProjPosition;
                uniform vec3 uColor;

                varying vec4 vColor;
                varying vec3 vNormal;
                varying vec4 vWorldPosition;
                varying vec4 vTexCoords;

                void main()
                {
                    //transform the position from world space into clip space and to the uv range [0,1]
                    vec2 uv = (vTexCoords.xy / vTexCoords.w) * 0.5 + 0.5;
                    vec4 outColor = texture2D(uTexture0, uv);
                    //don't render the texture on the back of the object
                    vec3 projectorDirection = normalize(uProjPosition - vWorldPosition.xyz);
                    float dotProduct = dot(vNormal, projectorDirection);
                    if(dotProduct < 0.0) outColor = vColor; //vec4(uColor, 1.0)*vColor;
                    gl_FragColor = outColor;
                }
            `,
        });

        return material;
    }

    setupSubjects() {
        const elements = new THREE.Group();
        const numElem = 500;
        const material = this.createMaterial();
        let geo1 = new THREE.BoxBufferGeometry(getRandom(0.1, 0.5));
        let geo2 = new THREE.TetrahedronBufferGeometry(getRandom(0.1, 1));
        let mesh1 = new THREE.Mesh(geo1, material);
        let mesh2 = new THREE.Mesh(geo2, material);
        for (let i = 0; i < numElem; i++) {
            const element = Math.random() > 0.5 ? mesh1.clone() : mesh2.clone();

            if (i < numElem * 0.3) {
                element.position.x = getRandom(-10, 10);
                element.position.y = getRandom(-10, 10);
                element.position.z = getRandom(-5.5, 0.5);
                element.scale.multiplyScalar(3);
            }
            else {
                element.position.x = getRandom(-20.5, 20.5);
                element.position.y = getRandom(-20, 20);
                element.position.z = getRandom(-0.5, 0.5);
            }
            element.rotation.x = getRandom(0, Math.PI * 2);
            element.rotation.y = getRandom(0, Math.PI * 2);
            element.rotation.z = getRandom(0, Math.PI * 2);

            elements.add(element);
            this.scene.add(elements);
            this.elements = elements;
        }

        this.scatterRandomGeometry();
    }

    scatterRandomGeometry() {
        const numMeshes = 100;
        const randMeshes = new THREE.Group();
        let geo = new THREE.TetrahedronBufferGeometry(1);
        const material = new THREE.MeshBasicMaterial({ wireframe: true });
        for (let i = 0; i < numMeshes; i++) {
            const mesh = new THREE.Mesh(geo, material);
            mesh.rotation.set(
                getRandom(0, Math.PI * 2),
                getRandom(0, Math.PI * 2),
                getRandom(0, Math.PI * 2));
            mesh.position.set(
                getRandom(-20.5, 20.5),
                getRandom(-20, 20),
                getRandom(-5, 1.5));
            mesh.scale.multiplyScalar(getRandom(0.3, 1));
            randMeshes.add(mesh);
        }
        this.scene.add(randMeshes);
        this.randMeshes = randMeshes;
    }

    animate(mesh) {
        this.isAnimating = true;
        const originalPosX = mesh.position.x;
        const originalPosY = mesh.position.y;
        const originalPosZ = mesh.position.z;
        const easing = 'elastic';
        let tween = gsap.timeline();
        tween.to(mesh.position, 10, { x: getRandom(-10, 10), y: getRandom(-10, 10), z: getRandom(0, -50), ease: easing })
            .to(mesh.position, 10, { x: originalPosX, y: originalPosY, z: originalPosZ, ease: easing });
        tween.eventCallback("onComplete", () => {
            this.isAnimating = false;
        });
    }

    rotate(mesh, speed) {
        let tween = gsap
            .to(mesh.rotation, speed, { y: mesh.rotation.y + Math.PI * 0.5, z: mesh.rotation.z + Math.PI * 0.5 });
        tween.play();
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onMouseDown(e) {
        if (this.isDisposed) return;
        if (this.isAnimating) return;
        for (let i = 0; i < 200; i++) //this.elements.children.length
        {
            //console.log(this.elements.children[i]);
            this.animate(this.elements.children[i]);
        }
        for (let i = 201; i < this.elements.children.length; i++) {
            this.rotate(this.elements.children[i], 5);
        }

        for (let i = 0; i < this.randMeshes.children.length; i++) {
            this.rotate(this.randMeshes.children[i], 10);
        }
    }


    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.material.dispose(); //TODO: fix mistake with this line
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
}

function getRandom(min, max) {
    return Math.random() * (max - min) + min;
};

